// ════════════════════════════════════════════════════════════════════════════
// Click-to-sort for the Activity Report tables.
//
// Every report table (State/UT breakdown, district drill-down, school list,
// theme breakdown) ships in ONE order decided by the server — participants
// descending. That hides the story in any other column: the Plantation report's
// Trees Planted column, for instance, is completely unordered, so the biggest
// planter can sit tenth. These helpers let any column header become a sort
// control without each table re-implementing it.
//
// Sorting is purely client-side over the rows already fetched. For paginated
// tables it therefore sorts the CURRENT PAGE only — pass the full list where
// that matters.
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useMemo, useCallback } from "react";

/**
 * @param rows        the array to sort
 * @param defaultKey  column key to sort by initially (null = server order)
 * @param defaultDir  "desc" | "asc"
 */
export function useSortableRows(rows, defaultKey = null, defaultDir = "desc") {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });

  const sorted = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!sort.key) return list; // untouched → keep the server's ordering
    const arr = [...list];
    arr.sort((a, b) => {
      const av = a?.[sort.key];
      const bv = b?.[sort.key];
      const aNum = av === "" || av == null ? null : Number(av);
      const bNum = bv === "" || bv == null ? null : Number(bv);
      const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);
      // Numeric compare when both sides are numbers (UDISE codes included);
      // otherwise a natural-order string compare so "Delhi" < "Goa".
      const cmp = bothNumeric
        ? aNum - bNum
        : String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true, sensitivity: "base" });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [rows, sort]);

  // First click on a numeric column shows the biggest values (desc); on a text
  // column it reads A→Z. Clicking the active column flips the direction.
  const toggle = useCallback((key, numeric = true) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: numeric ? "desc" : "asc" }));
  }, []);

  return { sorted, sort, toggle };
}

/**
 * A <th> that sorts the table when clicked.
 *
 * The cell keeps its native columnheader role (so `aria-sort` is actually valid
 * and announced), and the click target is a real <button> inside it — that gives
 * keyboard activation and focus for free instead of re-creating them on a <th>.
 */
export function SortTh({ label, sortKey, sort, onSort, className = "", numeric = true, title }) {
  const active = sort?.key === sortKey;
  const direction = active ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  return (
    <th
      className={`${className} ar-th-sort${active ? " is-sorted" : ""}`}
      aria-sort={direction}
    >
      <button
        type="button"
        className="ar-th-inner"
        onClick={() => onSort(sortKey, numeric)}
        title={title || `Sort by ${label}`}
      >
        <span>{label}</span>
        <span className="ar-sort-arrow" aria-hidden="true">{active ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}</span>
      </button>
    </th>
  );
}

/** Human-readable caption describing the current sort. */
export function sortCaption(sort, labels, fallback = "sorted by Total Participants") {
  if (!sort?.key || !labels?.[sort.key]) return fallback;
  return `sorted by ${labels[sort.key]} (${sort.dir === "asc" ? "low → high" : "high → low"})`;
}
